import React from "react";
import {
  Platform,
  View,
  type ViewStyle,
  type LayoutChangeEvent,
  StyleSheet,
  Animated,
  type DimensionValue,
} from "react-native";

import { useTheme, type Theme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";
import { useAnimatedNative } from "../util/useAnimatedNative";
import { ShimmerGradient } from "./ShimmerGradient";

// Allow padding, size, and border radius overrides.
export type SkeletonStyle = StrictPaddingStyle &
  Pick<ViewStyle, "height" | "width" | "borderRadius">;

export interface SkeletonProps {
  /**
   * The variant of the skeleton.
   */
  variant: SkeletonVariant;
  /**
   * The size of the skeleton.
   */
  size?: SkeletonSize;
  /**
   * The speed of the skeleton animation.
   */
  speed?: SkeletonAnimationSpeed;
  /**
   * The alignment of the skeleton.
   */
  alignment?: SkeletonAlignment;
  /**
   * The number of items to render.
   */
  count?: number;
  /**
   * The test ID of the skeleton.
   */
  testID?: string;

  /**
   * Defines the padding and size style overrides.
   */
  sx?: SkeletonStyle;

  /**
   * The accessibility label for screen readers.
   * Should be localized by the consumer.
   */
  ariaLabel?: string;

  /**
   * Whether the skeleton should be focusable by screen readers.
   * Defaults to true.
   */
  accessible?: boolean;
}

/**
 * Skeleton component for loading states.
 * @param props - The props for the skeleton.
 * @returns The skeleton component.
 * @example
 * <Skeleton variant={SkeletonVariant.HEADING} size={SkeletonSize.MD} />
 * @see https://www.figma.com/design/UQ0baUglOtMCWJ1jkwcSzW/Mobile-Apps-Design-System--MADS-?m=auto&node-id=6086-6234
 */
export function Skeleton({
  variant,
  size = SkeletonSize.MD,
  speed = SkeletonAnimationSpeed.NORMAL,
  alignment = SkeletonAlignment.LEFT,
  count = 1,
  testID,
  sx,
  ariaLabel,
  accessible = true,
}: SkeletonProps) {
  const {
    height: sxHeight,
    width: sxWidth,
    borderRadius: sxBorderRadius,
    ...sxContainer
  } = sx ?? {};
  const theme = useTheme();
  const animatedValue = React.useRef(new Animated.Value(0)).current;
  if (variant !== SkeletonVariant.TEXT) {
    count = 1;
  }

  const styles = StyleSheet.create({
    container: {
      gap: theme.spacing.s4,
      width: sxWidth ?? "100%",
    },
  });

  const useNativeDriver = useAnimatedNative();

  React.useEffect(() => {
    const speedValue = getSpeedValue(speed);

    // Reset animated value to 0 before starting new animation
    animatedValue.setValue(0);

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: speedValue,
          useNativeDriver: useNativeDriver,
        }),
        Animated.timing(animatedValue, {
          toValue: 0,
          duration: speedValue,
          useNativeDriver: useNativeDriver,
        }),
      ]),
    );

    animation.start();

    return () => {
      animation.stop();
    };
  }, [animatedValue, speed, useNativeDriver]);

  return (
    <View
      style={[styles.container, sxContainer]}
      accessibilityLabel={ariaLabel}
      accessible={accessible}
      {...Platform.select({
        web: accessible ? { role: "img" } : {},
        default: {},
      })}
    >
      {Array.from({ length: count }, (_, index) => (
        <SkeletonItem
          key={index}
          index={index}
          variant={variant}
          size={size}
          count={count}
          alignment={alignment}
          animatedValue={animatedValue}
          testID={testID}
          theme={theme}
          sxHeight={sxHeight}
          sxWidth={sxWidth}
          sxBorderRadius={sxBorderRadius}
        />
      ))}
    </View>
  );
}

interface SkeletonItemProps {
  index: number;
  variant: SkeletonVariant;
  size: SkeletonSize;
  count: number;
  alignment: SkeletonAlignment;
  animatedValue: Animated.Value;
  testID?: string;
  theme: Theme;
  sxHeight?: ViewStyle["height"];
  sxWidth?: ViewStyle["width"];
  sxBorderRadius?: ViewStyle["borderRadius"];
}

const SkeletonItem = ({
  index,
  variant,
  size,
  count,
  alignment,
  animatedValue,
  testID,
  theme,
  sxHeight,
  sxWidth,
  sxBorderRadius,
}: SkeletonItemProps) => {
  const [measuredWidth, setMeasuredWidth] = React.useState<number>(0);

  let widthPercent = 100;
  if (count > 1 && alignment !== SkeletonAlignment.JUSTIFY && variant === SkeletonVariant.TEXT) {
    widthPercent = defaultWidths[(index + defaultWidths.length) % defaultWidths.length] * 100;
  }

  const sizeValue = getSizeValue(size);
  const variantStyle = getVariantStyle(variant, sizeValue, theme);

  const itemStyle: ViewStyle = {
    ...variantStyle,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    ...(variant === SkeletonVariant.TEXT ? { width: `${widthPercent}%` as DimensionValue } : {}),
    overflow: "hidden",
    ...getAlignmentStyle(alignment),
    ...(sxHeight !== undefined ? { height: sxHeight } : {}),
    ...(sxWidth !== undefined ? { width: sxWidth } : {}),
    ...(sxBorderRadius !== undefined ? { borderRadius: sxBorderRadius } : {}),
  };

  // Use measured width for animation, fallback to fixed width for circle/square
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const width = (sxWidth ?? variantStyle.width) as number | string;
  const actualWidth = typeof width === "number" ? width : measuredWidth;

  // Reduce translation range so part of the gradient border remains visible at all times
  const translateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [-actualWidth * 0.5, actualWidth * 0.5],
  });

  const handleLayout = React.useCallback((event: LayoutChangeEvent) => {
    const { width: layoutWidth } = event.nativeEvent.layout;
    setMeasuredWidth(layoutWidth);
  }, []);

  const borderWidth = 1;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  const borderRadius = (sxBorderRadius ?? variantStyle.borderRadius) as number;

  return (
    <View style={[itemStyle]} testID={testID} onLayout={handleLayout}>
      {/* Animated gradient layer for border effect */}
      <Animated.View
        style={[
          StyleSheet.absoluteFillObject,
          {
            transform: [{ translateX }],
          },
        ]}
      >
        <ShimmerGradient
          colors={["transparent", theme.colors.border, theme.colors.border, "transparent"]}
          locations={[0.0, 0.4, 0.6, 1.0]}
        />
      </Animated.View>
      {/* Static inner fill that covers everything except the border */}
      <View
        style={{
          position: "absolute",
          top: borderWidth,
          left: borderWidth,
          right: borderWidth,
          bottom: borderWidth,
          backgroundColor: theme.colors.backgroundWeaker,
          borderRadius: Math.max(0, borderRadius - borderWidth),
        }}
      />
    </View>
  );
};

export enum SkeletonVariant {
  CIRCLE = "circle",
  TEXT = "text",
  SQUARE = "square",
  HEADING = "heading",
}

export enum SkeletonAlignment {
  LEFT = "left",
  RIGHT = "right",
  CENTER = "center",
  JUSTIFY = "justify",
}

export enum SkeletonSize {
  SM = 35,
  MD = 55,
}

export enum SkeletonAnimationSpeed {
  SNAPPY = 1000,
  QUICK = 1500,
  NORMAL = 2000,
  SLOW = 2500,
  SLOWEST = 3000,
}

const defaultWidths = [0.97, 1.0, 0.94, 0.9, 0.98, 0.95, 0.98, 0.4];

const getAlignmentStyle = (alignment: SkeletonAlignment): ViewStyle => {
  switch (alignment) {
    case SkeletonAlignment.RIGHT:
      return { alignSelf: "flex-end" };
    case SkeletonAlignment.CENTER:
      return { alignSelf: "center" };
    case SkeletonAlignment.JUSTIFY:
      return { alignSelf: "stretch" };
    default:
      return { alignSelf: "flex-start" };
  }
};

const getVariantStyle = (variant: SkeletonVariant, size: SkeletonSize, theme: Theme): ViewStyle => {
  const sizeValue = getSizeValue(size);

  const styles = StyleSheet.create({
    circle: {
      height: 95,
      width: 95,
      borderRadius: theme.borderRadius.full,
      backgroundColor: theme.colors.backgroundWeaker,
    },
    square: {
      height: 95,
      width: 95,
      borderRadius: theme.borderRadius.sm,
      backgroundColor: theme.colors.backgroundWeaker,
    },
    text: {
      height: 16,
      borderRadius: theme.borderRadius.sm,
      backgroundColor: theme.colors.backgroundWeaker,
    },
    headingSm: {
      height: 30,
      width: "100%",
      borderRadius: theme.borderRadius.sm,
      backgroundColor: theme.colors.backgroundWeaker,
    },
    headingMd: {
      height: 55,
      width: "100%",
      borderRadius: theme.borderRadius.sm,
      backgroundColor: theme.colors.backgroundWeaker,
    },
  });

  switch (variant) {
    case SkeletonVariant.CIRCLE:
      return styles.circle;
    case SkeletonVariant.SQUARE:
      return styles.square;
    case SkeletonVariant.TEXT:
      return styles.text;
    case SkeletonVariant.HEADING:
      return sizeValue === SkeletonSize.SM ? styles.headingSm : styles.headingMd;
    default:
      return styles.text;
  }
};

const getSizeValue = (size: SkeletonSize | string): SkeletonSize => {
  // Handle case where Storybook passes enum keys as strings
  if (typeof size === "string") {
    switch (size) {
      case "SM":
        return SkeletonSize.SM;
      case "MD":
        return SkeletonSize.MD;
      default:
        return SkeletonSize.MD;
    }
  }
  // If it's already a number, return it
  return size;
};

const getSpeedValue = (speed: SkeletonAnimationSpeed | string): number => {
  // Handle case where Storybook passes enum keys as strings
  if (typeof speed === "string") {
    switch (speed) {
      case "SNAPPY":
        return SkeletonAnimationSpeed.SNAPPY;
      case "QUICK":
        return SkeletonAnimationSpeed.QUICK;
      case "NORMAL":
        return SkeletonAnimationSpeed.NORMAL;
      case "SLOW":
        return SkeletonAnimationSpeed.SLOW;
      case "SLOWEST":
        return SkeletonAnimationSpeed.SLOWEST;
      default:
        return SkeletonAnimationSpeed.NORMAL;
    }
  }
  // If it's already a number, return it
  return speed;
};
